import { Component, Vue, Watch } from 'vue-property-decorator';
import { Auth, Logger } from 'aws-amplify';
import { Validations } from 'vuelidate-property-decorators';
import { required, email, minLength } from 'vuelidate/lib/validators';

const logger = new Logger('ForgotPassword');

@Component
export default class ForgotPassword extends Vue {
  private waitingCode = false;
  private doneReset = false;
  private loading = false;

  private showPassword = false;
  private capsWarning = false;

  private resendCooldown = 0;
  private resendTimer: number | null = null;

  private globalError = '';

  private form = {
    email: '',
    code: '',
    password: '',
    error: '',
  };

  // --- Vuelidate dynamiques selon l'étape ---
  @Validations()
  private validations() {
    if (this.waitingCode) {
      return {
        form: {
          email: { required, email, notExist: this.notExist },
          code: { required, minLength: minLength(6) },
          password: {
            required,
            minLength: minLength(8),
            containsUppercase: (v: string) => /[A-Z]/.test(v || ''),
            containsLowercase: (v: string) => /[a-z]/.test(v || ''),
            containsNumber: (v: string) => /[0-9]/.test(v || ''),
          },
        },
      };
    }
    return {
      form: {
        email: { required, email, notExist: this.notExist },
      },
    };
  }

  // --- Soumission ---
  public async onSubmit(event: Event): Promise<boolean> {
    event.preventDefault();
    this.globalError = '';
    this.$v.$touch();
    if (this.$v.$invalid) return false;

    try {
      this.loading = true;

      if (this.waitingCode) {
        // Étape 2 : confirmer et réinitialiser
        await Auth.forgotPasswordSubmit(this.form.email, this.form.code, this.form.password);
        this.doneReset = true;
      } else {
        // Étape 1 : demander le code
        await Auth.forgotPassword(this.form.email);
        this.waitingCode = true;
        this.startResendCooldown();
      }
    } catch (err: any) {
      logger.error({ err });
      this.handleError(err);
    } finally {
      this.loading = false;
    }
    return false;
  }

  public onCancel(event: Event): boolean {
    event.preventDefault();
    this.$emit('cancel');
    return false;
  }

  public toggleShowPassword() {
    this.showPassword = !this.showPassword;
  }

  @Watch('form.email')
  private onEmailChange() {
    this.form.error = '';
    this.globalError = '';
  }

  private notExist() {
    return this.form.error === '';
  }

  // --- Resend code ---
  private async resendCode() {
    if (this.resendCooldown > 0 || this.loading) return;
    try {
      this.loading = true;
      await Auth.forgotPassword(this.form.email);
      this.startResendCooldown();
    } catch (err: any) {
      logger.error({ err });
      this.handleError(err);
    } finally {
      this.loading = false;
    }
  }

  private startResendCooldown() {
    this.resendCooldown = 30; // 30s anti-spam
    if (this.resendTimer) window.clearInterval(this.resendTimer);
    this.resendTimer = window.setInterval(() => {
      this.resendCooldown -= 1;
      if (this.resendCooldown <= 0 && this.resendTimer) {
        window.clearInterval(this.resendTimer);
        this.resendTimer = null;
      }
    }, 1000) as unknown as number;
  }

  // --- Gestion erreurs lisibles ---
  private handleError(err: any) {
    const code = err?.code;
    if (code === 'UserNotFoundException') {
      this.form.error = 'userNotFoundException';
      this.globalError = this.$t('login.userNotFound') as string;
    } else if (code === 'CodeMismatchException') {
      this.globalError = (this.$t('forgotpassword.codeMismatch') as string) || 'Code invalide.';
    } else if (code === 'ExpiredCodeException') {
      this.globalError = (this.$t('forgotpassword.codeExpired') as string) || 'Code expiré.';
    } else {
      this.globalError = (this.$t('global.error') as string) || 'Une erreur est survenue. Réessayez.';
    }
  }

  // --- Indicateur de robustesse mdp (simple) ---
  get passwordLevel(): number {
    const v = this.form.password || '';
    let score = 0;
    if (v.length >= 8) score++;
    if (/[A-Z]/.test(v)) score++;
    if (/[a-z]/.test(v)) score++;
    if (/[0-9]/.test(v)) score++;
    if (/[^A-Za-z0-9]/.test(v)) score++; // bonus symboles
    // renvoie 0..5
    return score;
  }

  destroyed() {
    if (this.resendTimer) window.clearInterval(this.resendTimer);
  }
}
